import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  getAgentBillingStatus,
  deferAgentBillingMigration,
} from "../services/agentBillingStatusApi";

/**
 * Context משותף ל-billing status של הסוכן המחובר.
 *
 * מטרה: fetch יחיד ברמת ה-agent dashboard chrome — ממנו:
 *   1. AdminMessageBanner (המנגנון המאוחד) משלב billing_migration עם admin broadcasts
 *   2. BillingMigrationGateModal — פופאפ האכיפה (חובה / חסימה)
 *   3. AgentAccountOverview / route guard צורכים את הערך כדי לחסום Upgrade CTA / ניווט
 *
 * הכוונה היא *לא* להיות store מלא — רק cache של קריאה אחת מאוחדת
 * כך שלא יהיו כפילויות fetch/banner כש-AgentAccountOverview מורכב
 * בתוך AgentDashboardChrome.
 *
 * Session behavior:
 *   * ה-modal נפתח פעם אחת ב-session. אחרי defer מוצלח, `acknowledgedThisSession = true`
 *     עד reload/login חדש → לא נפתח שוב.
 *   * כש-isBlocked = true — ה-modal תמיד פתוח, בלי יכולת סגירה. אין דילוג ע״י sessionStorage.
 */

const AgentBillingStatusContext = createContext(null);

const SESSION_ACK_KEY = "hr_billing_migration_ack_v1";
const BILLING_STATUS_ERROR_COOLDOWN_MS = 60_000;
const AGENT_ORG_TOUR_ROUTE_PATH_RE = /\/organized-tours(?:\/|$)/;

function isAgentOrganizedTourRoutePathname(pathname) {
  return AGENT_ORG_TOUR_ROUTE_PATH_RE.test(String(pathname || ""));
}

/** @deprecated use isAgentOrganizedTourRoutePathname */
function isAgentOrganizedTourPaymentPathname(pathname) {
  return isAgentOrganizedTourRoutePathname(pathname);
}

function extractOrganizedTourPaymentOrderIdFromPath(pathname) {
  const m = String(pathname || "").match(/\/organized-tours\/payment\/([^/?#]+)/);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

/**
 * BE may return HTTP 200 with { ok: false, error_code, message, ...safeDefaults }.
 * Normalize to the stable billing snapshot shape consumed by the chrome.
 */
function normalizeAgentBillingStatusPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.ok === false) {
    return {
      agentId: raw.agentId ?? null,
      packageCode: null,
      effectivePackage: null,
      subscriptionStatus: null,
      billingMigrationRequired: false,
      hasActivePaymentCoverage: false,
      nextChargeAt: null,
      lastChargeAt: null,
      deferCount: 0,
      maxDefers: Number(raw.maxDefers ?? 4),
      isBlocked: false,
      allowDashboardAccess: true,
      mustOpenModal: false,
    };
  }
  const { ok: _ok, error_code: _ec, message: _msg, ...rest } = raw;
  return rest;
}

function areBillingStatusSnapshotsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.billingMigrationRequired === b.billingMigrationRequired &&
    a.subscriptionStatus === b.subscriptionStatus &&
    a.isBlocked === b.isBlocked &&
    a.mustOpenModal === b.mustOpenModal &&
    a.allowDashboardAccess === b.allowDashboardAccess &&
    Number(a.deferCount ?? 0) === Number(b.deferCount ?? 0) &&
    Number(a.maxDefers ?? 0) === Number(b.maxDefers ?? 0)
  );
}

function readSessionAck() {
  try {
    return window.sessionStorage.getItem(SESSION_ACK_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSessionAck(v) {
  try {
    if (v) window.sessionStorage.setItem(SESSION_ACK_KEY, "1");
    else window.sessionStorage.removeItem(SESSION_ACK_KEY);
  } catch {
    /* ignore (SSR / privacy mode) */
  }
}

export function AgentBillingStatusProvider({ children }) {
  const location = useLocation();
  const reloadCooldownUntilRef = useRef(0);
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [acknowledgedThisSession, setAcknowledgedThisSession] = useState(
    () => readSessionAck(),
  );
  const [deferring, setDeferring] = useState(false);

  const reload = useCallback(async () => {
    const pathname =
      typeof window !== "undefined" ? window.location.pathname : "";
    if (isAgentOrganizedTourRoutePathname(pathname)) {
      if (typeof console !== "undefined") {
        const oid = extractOrganizedTourPaymentOrderIdFromPath(pathname);
        console.info(
          "[ORG_TOUR_BILLING_STATUS_START] skip_fetch organized_tour_route visibility_or_reload",
        );
        console.info(
          `[ORG_TOUR_BILLING_STATUS_ORDER_CONTEXT] orderId=${oid ?? "none"} dealtype=OrganizedTour business_status=n/a completion_state=n/a paid=n/a remaining_balance=n/a`,
        );
      }
      return;
    }

    const now = Date.now();
    if (now < reloadCooldownUntilRef.current) {
      if (typeof console !== "undefined") {
        console.info(
          "[ORG_TOUR_BILLING_STATUS_START] skip_fetch server_error_cooldown_ms_remaining",
          reloadCooldownUntilRef.current - now,
        );
      }
      return;
    }

    setLoading(true);
    try {
      if (typeof console !== "undefined") {
        console.info(
          "[ORG_TOUR_BILLING_STATUS_START] fetch /api/agent/billing/status",
        );
      }
      const raw = await getAgentBillingStatus();
      if (raw?.ok === false && typeof console !== "undefined") {
        console.info(
          "[ORG_TOUR_BILLING_STATUS_RESPONSE] ok=false",
          raw.error_code,
          raw.message,
        );
      }
      const nextStatus = normalizeAgentBillingStatusPayload(raw);
      setBillingStatus((prev) =>
        areBillingStatusSnapshotsEqual(prev, nextStatus) ? prev : nextStatus
      );
      // אם הסטטוס כבר לא legacy_unbilled — לנקות את ה-ack (למקרה שהסוכן השלים
      // billing באותו session; אין סיבה להשאיר דגל ack "תקוע").
      if (nextStatus && nextStatus.billingMigrationRequired === false) {
        writeSessionAck(false);
        setAcknowledgedThisSession(false);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (!e?.response || (typeof status === "number" && status >= 500)) {
        reloadCooldownUntilRef.current = Date.now() + BILLING_STATUS_ERROR_COOLDOWN_MS;
      }
      if (typeof console !== "undefined") {
        console.warn(
          "[ORG_TOUR_BILLING_STATUS_ERROR]",
          e?.response?.status,
          e?.message || e,
          isAgentOrganizedTourRoutePathname(
            typeof window !== "undefined" ? window.location.pathname : "",
          )
            ? { note: "billing_status_does_not_block_organized_tour_checkout" }
            : undefined,
        );
      }
      // Fallback שקט — הקומפוננטה הקוראת לא שוברת מסך בגלל שגיאה כאן.
      setBillingStatus(null);
      if (typeof console !== "undefined") {
        const isTimeout =
          e?.code === "ECONNABORTED" ||
          String(e?.message || "").toLowerCase().includes("timeout");
        if (isTimeout) {
          console.info(
            "[AgentBillingStatusProvider] load timed out (will retry on visibility/focus):",
            e?.message || e,
          );
          return;
        }
        console.warn(
          "[AgentBillingStatusProvider] load failed:",
          e?.message || e,
        );
      }
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, []);

  /**
   * נקרא רק ב-click מפורש של "המשך למערכת".
   * אסור להפעיל אוטומטית (refresh / visibilitychange / bootstrap).
   */
  const defer = useCallback(async () => {
    setDeferring(true);
    try {
      const payload = await deferAgentBillingMigration();
      const nextStatus = normalizeAgentBillingStatusPayload(payload);
      setBillingStatus((prev) =>
        areBillingStatusSnapshotsEqual(prev, nextStatus) ? prev : nextStatus
      );
      writeSessionAck(true);
      setAcknowledgedThisSession(true);
      return payload;
    } finally {
      setDeferring(false);
    }
  }, []);

  useEffect(() => {
    const pathname = location.pathname || "";
    if (isAgentOrganizedTourRoutePathname(pathname)) {
      const oid = extractOrganizedTourPaymentOrderIdFromPath(pathname);
      if (typeof console !== "undefined") {
        console.info(
          "[ORG_TOUR_BILLING_STATUS_START] skip_mount_fetch organized_tour_route",
        );
        console.info(
          `[ORG_TOUR_BILLING_STATUS_ORDER_CONTEXT] orderId=${oid ?? "none"} dealtype=OrganizedTour business_status=n/a completion_state=n/a paid=n/a remaining_balance=n/a`,
        );
      }
      setLoading(false);
      setLoadedOnce(true);
      return;
    }
    reload();
  }, [location.pathname, reload]);

  // רענון כש-tab חוזר ל-visible — עקבי עם ההתנהגות הקיימת ב-AgentAccountOverview.
  // חשוב: זה לא מקטין/מגדיל defer count, רק רענון read-only.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") {
        reload();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [reload]);

  // Post-HYP return: אם חוזרים מ-vault callback עם query params של הצלחה/כשל,
  // נרענן במפורש וננקה את ה-URL כדי שלא יישאר "נעול" על state ישן.
  // ה-BE כבר עשה finalize אוטומטית ב-/api/payment-methods/hyp/success.
  //
  // שני פרוטוקולי query נתמכים במקביל:
  //   * `billingSetup=ok|cancelled|error`  — ה-flow החדש של legacy_billing_migration
  //     (מחזיר אל /p/dashboard/home ולכן חייב פרם ייעודי שלא יתנגש עם מסכי
  //     הגדרות אחרים שמשתמשים ב-`status`).
  //   * `status=ok|cancelled` + `paymentMethodId` — ה-flow הקלאסי של vault מהגדרות;
  //     נשמר backward-compatible.
  useEffect(() => {
    try {
      const path = String(window.location?.pathname || "");
      const isDedicatedVaultReturn = path === "/p/dashboard/payment-method-return";
      if (isDedicatedVaultReturn) {
        // Dedicated vault-return screen owns the refresh lifecycle to avoid duplicate calls.
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const billingSetup = params.get("billingSetup");
      const hasLegacyReturn =
        billingSetup === "ok" ||
        billingSetup === "cancelled" ||
        billingSetup === "error";
      const hasClassicReturn =
        params.has("paymentMethodId") ||
        params.get("status") === "ok" ||
        params.get("status") === "cancelled" ||
        params.get("status") === "failed" ||
        params.get("status") === "error";
      if (!hasLegacyReturn && !hasClassicReturn) return;
      // Clean URL first so reloads/back buttons don't re-trigger logic.
      params.delete("paymentMethodId");
      params.delete("status");
      params.delete("reason");
      params.delete("billingSetup");
      const cleanSearch = params.toString();
      const nextUrl =
        window.location.pathname + (cleanSearch ? `?${cleanSearch}` : "");
      window.history.replaceState(null, "", nextUrl);
      reload();
    } catch {
      /* ignore — SSR / non-browser / exotic URL */
    }
  }, [reload]);

  const subStatusLc = String(billingStatus?.subscriptionStatus || "")
    .trim()
    .toLowerCase();

  const showBillingMigrationGate =
    billingStatus?.billingMigrationRequired === true ||
    subStatusLc === "legacy_unbilled";

  const isBlocked = billingStatus?.isBlocked === true;

  // כלל הצגת ה-modal:
  //   - חסום → תמיד להציג (אין דילוג)
  //   - אחרת → להציג אם השרת אמר mustOpenModal ועדיין לא נלחץ "המשך" ב-session הזה
  const shouldOpenMigrationModal =
    showBillingMigrationGate &&
    (isBlocked || (billingStatus?.mustOpenModal === true && !acknowledgedThisSession));

  const value = useMemo(
    () => ({
      billingStatus,
      loading,
      loadedOnce,
      deferring,
      reload,
      defer,
      // Derived flags consumed across the app.
      showBillingMigrationGate,
      isBlocked,
      allowDashboardAccess:
        billingStatus?.allowDashboardAccess !== false, // default TRUE when unknown
      shouldOpenMigrationModal,
      deferCount: Number(billingStatus?.deferCount ?? 0),
      maxDefers: Number(billingStatus?.maxDefers ?? 4),
    }),
    [
      billingStatus,
      loading,
      loadedOnce,
      deferring,
      reload,
      defer,
      showBillingMigrationGate,
      isBlocked,
      shouldOpenMigrationModal,
    ],
  );

  return (
    <AgentBillingStatusContext.Provider value={value}>
      {children}
    </AgentBillingStatusContext.Provider>
  );
}

/**
 * Hook לצריכת billing status.
 * אם נקרא מחוץ לפרוביידר — מחזיר ברירת מחדל לא-דולקת, כדי שקומפוננטות
 * בודדות שמוצגות גם מחוץ לדאשבורד (למשל בדיקות / סיפורים) לא ישברו.
 */
export function useAgentBillingStatus() {
  const ctx = useContext(AgentBillingStatusContext);
  if (!ctx) {
    return {
      billingStatus: null,
      loading: false,
      loadedOnce: false,
      deferring: false,
      reload: () => {},
      defer: async () => null,
      showBillingMigrationGate: false,
      isBlocked: false,
      allowDashboardAccess: true,
      shouldOpenMigrationModal: false,
      deferCount: 0,
      maxDefers: 4,
    };
  }
  return ctx;
}

export default AgentBillingStatusContext;
